"use client";

import * as React from "react";
import { HelpCircle } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/* ──────────────────────────────────────────────────────────────────────────
 * NKHelpTip
 *
 * Small `?` icon next to a label that reveals a popover with extra context
 * (CRA-rule explanations, source notes, "what does this mean" definitions).
 *
 * The NK rule (see the `quality-advocacy` skill (dq-core) and
 * `frontend/ux-writing-and-microcopy.mdc`): never stack amber callout
 * strips, info banners, or explanatory paragraphs right inside a form.
 * Hide depth behind a `?` so the resting UI stays calm — the user clicks
 * for context when they want it.
 *
 * For tax-jargon labels (ACB, ROC, ITA s. 20(1)(c)), pair the label with
 * an NKHelpTip carrying the plain-English explanation. The label stays
 * domain-accurate; the tip carries the translation.
 *
 * Examples:
 *   <NKHelpTip ariaLabel="About ROC" title="Return of capital"
 *     body="A fund distribution that's not income — it reduces your ACB
 *     and (per Van Steenis) the deductible portion of your loan unless
 *     reinvested in an eligible asset." />
 * ────────────────────────────────────────────────────────────────────────── */

export interface NKHelpTipProps {
  /** Required accessible label for the trigger button. */
  ariaLabel: string;
  /** Optional title rendered in bold above the body. */
  title?: string;
  /** Body text or content. Used when `children` is not provided. */
  body?: React.ReactNode;
  /** Popover side alignment. Defaults to `start`. */
  align?: "start" | "center" | "end";
  /** Trigger size. `sm` for inline use, `md` for card-corner placement. */
  size?: "sm" | "md";
  /** Tailwind override for popover content width / padding. */
  contentClassName?: string;
  /** Rich content. Overrides `title`/`body` when set. */
  children?: React.ReactNode;
}

export function NKHelpTip({
  ariaLabel,
  title,
  body,
  align = "start",
  size = "sm",
  contentClassName,
  children,
}: NKHelpTipProps) {
  const trigger =
    size === "md" ? (
      <button
        type="button"
        aria-label={ariaLabel}
        className="border-input bg-background text-muted-foreground hover:border-ring hover:text-foreground inline-flex size-7 items-center justify-center rounded-full border transition-colors"
      >
        <HelpCircle className="size-4" aria-hidden />
      </button>
    ) : (
      <button
        type="button"
        aria-label={ariaLabel}
        className="text-muted-foreground hover:text-foreground inline-flex size-4 items-center justify-center rounded-full transition-colors"
      >
        <HelpCircle className="size-3.5" aria-hidden />
      </button>
    );

  return (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align={align}
        className={cn("w-72 text-xs leading-relaxed", contentClassName)}
      >
        {children ?? (
          <>
            {title ? <p className="text-foreground font-semibold">{title}</p> : null}
            {body ? (
              <p className={cn("text-muted-foreground", title ? "mt-1.5" : undefined)}>{body}</p>
            ) : null}
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
